import Component from '@glimmer/component'
import { t } from 'ember-intl'
import type LogoutService from 'teacher-ui-client/services/logout'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import type { TOC } from '@ember/component/template-only'
import abcCommercial from '@images/logos/abc-commercial.svg'

interface Signature {
  Args: {
    countryCode?: string
  }
  Element: HTMLElement
  Blocks: {
    default: []
  }
}

interface FooterLiSignature {
  Element: HTMLLIElement
  Args: {
    clickThroughUrl?: string
  }
  Blocks: {
    default: []
  }
}

export const FooterLi: TOC<FooterLiSignature> = <template>
  <li class="inline-block" data-test="footer-li" ...attributes>
    {{#if @clickThroughUrl}}
      <a data-test-click-through-url href={{@clickThroughUrl}}>
        {{yield}}
      </a>
    {{else}}
      {{yield}}
    {{/if}}
  </li>
</template>

export class HomeschoolFooter extends Component<Signature> {
  @service logout!: LogoutService

  get showCCPALinks() {
    return this.args.countryCode === 'US'
  }

  logoutAndRedirectToPuiDash = () => {
    void this.logout.logoutAndRedirectToReadingeggs('/parent_ui/redirect/home')
  }

  logoutAndRedirectToPuiContactForm = () => {
    void this.logout.logoutAndRedirectToReadingeggs('/parent_ui/contact-us')
  }

  get copyrightYear(): string {
    const today = new Date()
    const currentYear = today.getFullYear()
    return `© ${currentYear}`
  }

  get isAu(): boolean {
    return this.args.countryCode === 'AU'
  }

  <template>
    <footer
      class="via-apple-green-500 bg-linear-to-b from-[#b2d12d] to-[#41912d] text-center font-bold text-white print:hidden"
      data-test-homeschool-footer
      ...attributes
    >
      <div
        class="container mx-auto cursor-pointer p-5 [text-shadow:0_1px_2px_rgb(72_68_65)] md:flex md:flex-wrap md:space-x-6 [&_a]:text-white"
      >
        <div class="space-y-3 md:w-1/4">
          <p>
            <a
              aria-label={{t "components.footerMain.aboutUs.displayText"}}
              href={{t "components.footerMain.aboutUs.url"}}
            >
              <span
                aria-label={{t "components.footerMain.aboutUs.displayText"}}
                class="sprite blake-sprite mx-auto block"
                role="img"
              ></span>
            </a>
          </p>
          <p>
            {{#if this.isAu}}
              <a href={{t "components.footerMain.partners.webAddress"}} rel="noopener noreferrer" target="_blank">
                <span class="mx-auto block">
                  <img
                    alt={{t "components.footerMain.partners.aboutText"}}
                    class="mx-auto h-5 drop-shadow-xs"
                    data-test-partner-logo="abc commercial"
                    src={{abcCommercial}}
                  />
                </span>
                <span class="mt-2 inline-block">{{t "components.footerMain.partners.name"}}</span>
              </a>
            {{/if}}
          </p>
        </div>
        <div class="md:w-1/2">
          <ul class="space-y-3 space-x-3">
            <FooterLi @clickThroughUrl={{t "components.footerMain.copyright.url"}}>
              {{this.copyrightYear}}
            </FooterLi>
            <FooterLi @clickThroughUrl={{t "components.footerMain.aboutUs.url"}}>
              {{t "components.footerMain.aboutUs.displayText"}}
            </FooterLi>
            <FooterLi @clickThroughUrl={{t "components.footerMain.terms.url"}}>
              {{t "components.footerMain.terms.displayText"}}
            </FooterLi>
            <FooterLi @clickThroughUrl={{t "components.footerMain.privacy.url"}}>
              {{t "components.footerMain.privacy.displayText"}}
            </FooterLi>

            {{#if this.isAu}}
              <FooterLi @clickThroughUrl={{t "components.footerMain.partners.aboutUrl"}}>
                {{t "components.footerMain.partners.aboutText"}}
              </FooterLi>
            {{/if}}
            {{#if this.showCCPALinks}}
              <FooterLi
                @clickThroughUrl={{t "components.footerMain.ccpa.url"}}
                data-test-homeschool-ccpa-link
                class="block!"
              >
                {{t "components.footerMain.ccpa.displayText"}}
              </FooterLi>
            {{/if}}
            <FooterLi @clickThroughUrl={{t "components.footerMain.refund.url"}}>
              {{t "components.footerMain.refund.displayText"}}
            </FooterLi>

            <FooterLi {{on "click" this.logoutAndRedirectToPuiContactForm}}>
              {{t "components.footerMain.contact.displayText"}}
            </FooterLi>

            <FooterLi {{on "click" this.logoutAndRedirectToPuiDash}}>
              {{t "components.footerMain.logout.displayText"}}
            </FooterLi>
          </ul>
          {{yield}}
        </div>
        <div class="flex-1 md:w-1/4"></div>
      </div>
    </footer>
  </template>
}

export default HomeschoolFooter
